import React, { useRef, useEffect, useContext, useCallback } from 'react';
import { AppContext } from '../AppContext';
import { blendColors } from '../helpers/constants';

const HEIGHT = 21;
const LIGHTGRAY = [204, 204, 204]; // #DDDDDD — text without sources
const GRAY = [187, 187, 187];      // #BBBBBB — border
const RED = [190, 10, 0];
const GREEN = [10, 190, 0];
const EXCLUDED = [240, 240, 240];  // #F0F0F0 — excluded regions

/**
 * Dynamic report overview canvas that mirrors CreateReportOverviewImage() PHP logic.
 * Uses state.reportMarkup (flat token array from GetReportMarkup API) where each
 * token has PIdx (plagiarised), ERIdx (excluded region).
 * Renders a color bar plus a semi-transparent viewport indicator rectangle.
 */
export default function ReportOverviewCanvas({ style }) {
    const state = useContext(AppContext);
    const canvasRef = useRef(null);
    // Stores {map: {pIdx → {start,end}}, totalChars} so drawViewport can interpolate
    const tokenCharMapRef = useRef(null);
    // Stores the last-built sorted anchor array [{scrollOffset, charPos}] for click→scroll
    const anchorsRef = useRef(null);
    // Stores {x, w} of the last painted caret rect for drag hit-testing
    const caretRef = useRef(null);
    // Drag state: null when idle, object when dragging
    const dragRef = useRef(null);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const parent = canvas.parentElement;
        const width = parent ? parent.clientWidth : canvas.clientWidth;
        if (width <= 2) return;

        canvas.width = width;
        canvas.height = HEIGHT;

        const ctx = canvas.getContext('2d');

        const markup = state.reportMarkup;

        // --- Draw plain gray bar when no data yet ---
        if (!markup || markup.length === 0) {
            tokenCharMapRef.current = null;
            ctx.fillStyle = `rgb(${LIGHTGRAY.join(',')})`;
            ctx.fillRect(0, 0, width, HEIGHT);
            ctx.strokeStyle = `rgb(${GRAY.join(',')})`;
            ctx.lineWidth = 1;
            ctx.strokeRect(0.5, 0.5, width - 1, HEIGHT - 1);
            drawViewport(ctx, width, tokenCharMapRef, anchorsRef, caretRef);
            return;
        }

        // --- Statistics / Text-Overlay mode ---
        const sourcesView = state.ui && state.ui.sourcesView;
        if (sourcesView === 'statistics') {
            const overlayStats = state.overlayStatistics;
            if (!overlayStats || !overlayStats.phrases || overlayStats.phrases.length === 0) {
                tokenCharMapRef.current = null;
                ctx.fillStyle = `rgb(${LIGHTGRAY.join(',')})`;
                ctx.fillRect(0, 0, width, HEIGHT);
                ctx.strokeStyle = `rgb(${GRAY.join(',')})`;
                ctx.lineWidth = 1;
                ctx.strokeRect(0.5, 0.5, width - 1, HEIGHT - 1);
                drawViewport(ctx, width, tokenCharMapRef, anchorsRef, caretRef);
                return;
            }

            const statsType = (state.ui && state.ui.statisticsType) || 'kil';
            const statsMode = (state.ui && state.ui.statisticsMode) || 'md';
            const statsTypeData = state.ui && state.ui.textOverlay && state.ui.textOverlay[statsType];
            const colorScale = statsTypeData ? statsTypeData.colors : null;
            const valueScale = statsTypeData ? statsTypeData.values : null;
            const statsArray = overlayStats[statsType];

            const phrases = overlayStats.phrases;
            const lastPhrase = phrases[phrases.length - 1];
            const totalCharsStats = lastPhrase.s + lastPhrase.l;
            if (totalCharsStats === 0) return;

            const factStats = (width - 2) / totalCharsStats;
            const pixelsStats = new Uint8ClampedArray(width * HEIGHT * 4);

            const setColStats = (x, r, g, b) => {
                for (let y = 0; y < HEIGHT; y++) {
                    const idx = (y * width + x) * 4;
                    pixelsStats[idx]     = r;
                    pixelsStats[idx + 1] = g;
                    pixelsStats[idx + 2] = b;
                    pixelsStats[idx + 3] = 255;
                }
            };

            // Fill center columns lightgray (1-pixel border left and right stay 0/transparent)
            for (let x = 1; x < width - 1; x++) {
                setColStats(x, LIGHTGRAY[0], LIGHTGRAY[1], LIGHTGRAY[2]);
            }

            // Excluded byte ranges from the overlay stats response
            const excludeRegionsStats = overlayStats.excludeRegions || [];
            const isInExcludeRegionStats = (byteStart, byteEnd) =>
                excludeRegionsStats.some(r => byteStart < r.end && byteEnd > r.start);

            for (let i = 0; i < phrases.length; i++) {
                const phrase = phrases[i];
                const xStart = Math.max(1, Math.round(phrase.s * factStats) + 1);
                const xEnd   = Math.min(width - 1, Math.round((phrase.s + phrase.l) * factStats) + 1);

                if (phrase.ex || isInExcludeRegionStats(phrase.s, phrase.s + phrase.l)) {
                    // Excluded region — paint in excluded color
                    for (let x = xStart; x < xEnd; x++) {
                        setColStats(x, EXCLUDED[0], EXCLUDED[1], EXCLUDED[2]);
                    }
                    continue;
                }

                const statEntry = statsArray && statsArray[i];
                const statValue = statEntry != null ? statEntry[statsMode] : null;
                if (statValue == null) continue;

                const hexColor = blendColors(statValue, colorScale, valueScale);
                const r = parseInt(hexColor.slice(1, 3), 16);
                const g = parseInt(hexColor.slice(3, 5), 16);
                const b = parseInt(hexColor.slice(5, 7), 16);

                for (let x = xStart; x < xEnd; x++) {
                    setColStats(x, r, g, b);
                }
            }

            tokenCharMapRef.current = { map: {}, totalChars: totalCharsStats };
            // Clear stale source-mode anchors so click/drag use linear fallback
            anchorsRef.current = null;
            ctx.putImageData(new ImageData(pixelsStats, width, HEIGHT), 0, 0);

            const fadeRowsStats = Math.floor(HEIGHT / 3.0);
            for (let y = 0; y < fadeRowsStats; y++) {
                const gdAlpha = 90 - 9 * y;
                const cssOpacity = gdAlpha / 127;
                ctx.fillStyle = `rgba(255,255,255,${cssOpacity.toFixed(3)})`;
                ctx.fillRect(0, y, width, 1);
            }

            ctx.strokeStyle = `rgb(${GRAY.join(',')})`;
            ctx.lineWidth = 1;
            ctx.strokeRect(0.5, 0.5, width - 1, HEIGHT - 1);
            drawViewport(ctx, width, tokenCharMapRef, anchorsRef, caretRef);
            return;
        }

        // Build SId → source map for accepted (green) lookup.
        // Keying by Id (= SId) is correct because accepted status is per-source,
        // not per phrase. Multiple sources can share the same PIdx, so keying by
        // PIdx would overwrite entries and lose accepted status.
        const sourceById = {};
        if (state.sources) {
            state.sources.forEach(src => {
                if (src.Id != null) sourceById[src.Id] = src;
            });
        }

        // Build a flat char-level color array by iterating tokens and accumulating char positions
        // Each token.T has a text length; positions are character-based (like PHP's word-based array)
        let totalChars = 0;
        for (let i = 0; i < markup.length; i++) {
            totalChars += (markup[i].T || '').length;
        }
        if (totalChars === 0) totalChars = markup.length;

        // PHP: fact = (xres - 2) / length
        const fact = (width - 2) / totalChars;

        // Allocate RGBA pixel buffer
        const pixels = new Uint8ClampedArray(width * HEIGHT * 4);

        // Helper: fill a vertical column
        const setColumn = (x, r, g, b, a = 255) => {
            for (let y = 0; y < HEIGHT; y++) {
                const idx = (y * width + x) * 4;
                pixels[idx]     = r;
                pixels[idx + 1] = g;
                pixels[idx + 2] = b;
                pixels[idx + 3] = a;
            }
        };

        // Fill all pixels lightgray first
        for (let x = 0; x < width; x++) {
            setColumn(x, LIGHTGRAY[0], LIGHTGRAY[1], LIGHTGRAY[2]);
        }

        // Paint each token's character range onto the canvas
        // Build {SId}_{PIdx} → {start,end} map for viewport interpolation.
        // Key must include SId because multiple sources can share the same PIdx
        // (they belong to the same overlapping plagiarism region). Using only PIdx
        // would cause the entry to accumulate the entire document range, making the
        // viewport caret jump wildly.
        const pIdxCharMap = {};
        let charPos = 0;
        for (let i = 0; i < markup.length; i++) {
            const token = markup[i];
            const tokenLen = (token.T || '').length;
            if (tokenLen === 0) continue;

            if (token.PIdx != null && token.SId != null) {
                const key = `${token.SId}_${token.PIdx}`;
                if (pIdxCharMap[key] == null) {
                    pIdxCharMap[key] = { start: charPos, end: charPos + tokenLen };
                } else {
                    pIdxCharMap[key].end = charPos + tokenLen;
                }
            }

            let color = null; // null = lightgray (already filled)

            if (token.ERIdx != null) {
                color = EXCLUDED;
            } else if (token.PIdx != null) {
                const src = sourceById[token.SId];
                color = (src && src.Accepted == 1) ? GREEN : RED;
            }

            if (color) {
                // Map char range [charPos, charPos+tokenLen) to pixel range
                const xStart = Math.round(charPos * fact) + 1;
                const xEnd   = Math.round((charPos + tokenLen) * fact) + 1;
                for (let x = xStart; x < xEnd && x < width - 1; x++) {
                    setColumn(x, color[0], color[1], color[2]);
                }
            }

            charPos += tokenLen;
        }
        tokenCharMapRef.current = { map: pIdxCharMap, totalChars };

        // Write fully-opaque colored pixels to canvas
        const imageData = new ImageData(pixels, width, HEIGHT);
        ctx.putImageData(imageData, 0, 0);

        // PHP transparency fade: top yres/3 rows painted with white at decreasing opacity
        // PHP: imagecolorallocatealpha(255,255,255, 90-9*i) where GD alpha 0=opaque, 127=transparent
        // Row 0: GD alpha=90 → cssOpacity=(90/127)≈0.71 (lots of white)
        // Row 4: GD alpha=54 → cssOpacity=(54/127)≈0.43 (less white)
        const fadeRows = Math.floor(HEIGHT / 3.0);
        for (let y = 0; y < fadeRows; y++) {
            const gdAlpha = 90 - 9 * y;
            const cssOpacity = gdAlpha / 127;
            ctx.fillStyle = `rgba(255,255,255,${cssOpacity.toFixed(3)})`;
            ctx.fillRect(0, y, width, 1);
        }

        // Gray border rectangle (PHP: imagerectangle)
        ctx.strokeStyle = `rgb(${GRAY.join(',')})`;
        ctx.lineWidth = 1;
        ctx.strokeRect(0.5, 0.5, width - 1, HEIGHT - 1);

        // Viewport indicator drawn on top
        drawViewport(ctx, width, tokenCharMapRef, anchorsRef, caretRef);

    }, [state.reportMarkup, state.sources,
        state.ui && state.ui.showTextWithoutMarkup,
        state.ui && state.ui.showLinebreaks,
        state.ui && state.ui.sourcesView,
        state.ui && state.ui.statisticsType,
        state.ui && state.ui.statisticsMode,
        state.overlayStatistics]);

    // --- Click / drag handler ---
    // Clicking outside the caret: center that position in the viewport.
    // Clicking on the caret and dragging: move the caret (scroll the report).
    const handleMouseDown = useCallback((e) => {
        const canvas = canvasRef.current;
        const charMapData = tokenCharMapRef.current;
        if (!canvas || !charMapData || !charMapData.totalChars) return;

        const canvasRect = canvas.getBoundingClientRect();
        const canvasX = e.clientX - canvasRect.left;
        const innerWidth = canvasRect.width - 2;
        if (innerWidth <= 0) return;

        const el = document.getElementById('report-text-scroll');
        if (!el) return;

        const caret = caretRef.current;
        const isOnCaret = caret && canvasX >= caret.x && canvasX <= caret.x + caret.w;

        // If clicking outside the caret: jump instantly to that position first,
        // then fall through to start dragging from the new position.
        if (!isOnCaret) {
            const charPos = Math.max(0, Math.min(charMapData.totalChars,
                (canvasX - 1) / innerWidth * charMapData.totalChars));
            const anchorDataJump = anchorsRef.current;
            let jumpScroll;
            if (anchorDataJump && anchorDataJump.anchors && anchorDataJump.anchors.length >= 2) {
                const scrollOffset = charToScrollStandalone(charPos, anchorDataJump.anchors);
                jumpScroll = Math.max(0, Math.min(anchorDataJump.scrollHeight - el.clientHeight,
                    scrollOffset - el.clientHeight / 2));
            } else {
                jumpScroll = Math.max(0, charPos / charMapData.totalChars * el.scrollHeight - el.clientHeight / 2);
            }
            el.scrollTop = jumpScroll;
        }

        // Drag: prevent text selection, record start state (always, whether we jumped or not)
        e.preventDefault();
        const anchorData = anchorsRef.current;
        const startCharAtTop = anchorData && anchorData.anchors
            ? scrollToCharStandalone(el.scrollTop, anchorData.anchors)
            : (el.scrollTop / el.scrollHeight) * charMapData.totalChars;

        dragRef.current = {
            startCanvasX: canvasX,
            startCharAtTop,
            totalChars: charMapData.totalChars,
            innerWidth,
            anchorData,
            scrollHeight: el.scrollHeight,
            clientHeight: el.clientHeight,
        };
        canvas.style.cursor = 'grabbing';

        const handleDocMove = (me) => {
            const drag = dragRef.current;
            if (!drag) return;
            const elNow = document.getElementById('report-text-scroll');
            if (!elNow) return;
            const deltaX = (me.clientX - canvasRect.left) - drag.startCanvasX;
            const deltaChar = deltaX / drag.innerWidth * drag.totalChars;
            const newCharAtTop = drag.startCharAtTop + deltaChar;
            let targetScrollTop;
            if (drag.anchorData && drag.anchorData.anchors) {
                targetScrollTop = charToScrollStandalone(newCharAtTop, drag.anchorData.anchors);
            } else {
                targetScrollTop = newCharAtTop / drag.totalChars * drag.scrollHeight;
            }
            elNow.scrollTop = Math.max(0, Math.min(drag.scrollHeight - drag.clientHeight, targetScrollTop));
        };

        const handleDocUp = () => {
            dragRef.current = null;
            if (canvas) canvas.style.cursor = 'pointer';
            document.removeEventListener('mousemove', handleDocMove);
            document.removeEventListener('mouseup', handleDocUp);
        };

        document.addEventListener('mousemove', handleDocMove);
        document.addEventListener('mouseup', handleDocUp);
    }, []);

    // Cursor: 'grab' when hovering the caret, 'pointer' elsewhere
    const handleMouseMove = useCallback((e) => {
        if (dragRef.current) return; // already grabbing
        const canvas = canvasRef.current;
        const caret = caretRef.current;
        if (!canvas || !caret) return;
        const canvasRect = canvas.getBoundingClientRect();
        const canvasX = e.clientX - canvasRect.left;
        canvas.style.cursor = (canvasX >= caret.x && canvasX <= caret.x + caret.w) ? 'grab' : 'pointer';
    }, []);

    // --- Redraw on data change ---
    useEffect(() => {
        draw();
    }, [draw]);

    // --- Scroll listener: attach to the report text panel by id ---
    // Re-subscribes whenever data or display mode changes (element is remounted on mode switch)
    const reportDisplayMode = state.ui && state.ui.reportDisplayMode;
    const sourcesView = state.ui && state.ui.sourcesView;
    useEffect(() => {
        const el = document.getElementById('report-text-scroll');
        if (!el) return;
        const handleScroll = () => draw();
        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, [draw, reportDisplayMode, sourcesView]);

    // --- ResizeObserver to redraw when panel width changes ---
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !canvas.parentElement) return;
        const observer = new ResizeObserver(() => draw());
        observer.observe(canvas.parentElement);
        return () => observer.disconnect();
    }, [draw]);

    return (
        <canvas
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            style={{
                display: 'block',
                width: '100%',
                marginTop: 10,
                marginBottom: 5,
                cursor: 'pointer',
                ...style,
            }}
        />
    );
}

// ---------------------------------------------------------------------------
// Standalone interpolation helpers
// ---------------------------------------------------------------------------

// scrollOffset → charPos  (anchors sorted by scrollOffset)
function scrollToCharStandalone(offset, anchors) {
    if (offset <= anchors[0].scrollOffset) return anchors[0].charPos;
    if (offset >= anchors[anchors.length - 1].scrollOffset) return anchors[anchors.length - 1].charPos;
    let lo = 0, hi = anchors.length - 1;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (anchors[mid].scrollOffset <= offset) lo = mid; else hi = mid;
    }
    const d = anchors[hi].scrollOffset - anchors[lo].scrollOffset;
    const t = d === 0 ? 0 : (offset - anchors[lo].scrollOffset) / d;
    return anchors[lo].charPos + t * (anchors[hi].charPos - anchors[lo].charPos);
}

// charPos → scrollOffset  (anchors also monotonically increasing in charPos)
function charToScrollStandalone(charPos, anchors) {
    if (charPos <= anchors[0].charPos) return anchors[0].scrollOffset;
    if (charPos >= anchors[anchors.length - 1].charPos) return anchors[anchors.length - 1].scrollOffset;
    let lo = 0, hi = anchors.length - 1;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (anchors[mid].charPos <= charPos) lo = mid; else hi = mid;
    }
    const d = anchors[hi].charPos - anchors[lo].charPos;
    const t = d === 0 ? 0 : (charPos - anchors[lo].charPos) / d;
    return anchors[lo].scrollOffset + t * (anchors[hi].scrollOffset - anchors[lo].scrollOffset);
}

// ---------------------------------------------------------------------------
// Draws the semi-transparent viewport indicator rectangle.
//
// Strategy: plag spans are never truncated and reflow with the text, so their
// DOM vertical positions give accurate anchor points for mapping
// scrollOffset → charPosition. We interpolate between these anchors to find
// the character range currently visible, then draw the canvas rectangle from
// those char positions. This correctly handles both text truncation (only clean
// text is truncated) and line-break rendering (vertical space ≠ char count).
// ---------------------------------------------------------------------------
function drawViewport(ctx, width, tokenCharMapRef, anchorsRef, caretRef) {
    const el = document.getElementById('report-text-scroll');
    if (!el) return;

    const { scrollHeight, clientHeight, scrollTop } = el;
    if (scrollHeight <= clientHeight) return;

    const innerWidth = width - 2;
    const charMapData = tokenCharMapRef && tokenCharMapRef.current;

    // Fallback: linear scroll ratio (works when no truncation / line-breaks)
    if (!charMapData || !charMapData.totalChars) {
        paintRect(ctx, width,
            Math.round(scrollTop / scrollHeight * innerWidth) + 1,
            Math.max(2, Math.round(clientHeight / scrollHeight * innerWidth)),
            caretRef);
        return;
    }

    const { map, totalChars } = charMapData;
    const fact = innerWidth / totalChars;

    // Query all rendered plag spans (format: {SId}_source_phase_{PIdx})
    const spans = Array.from(el.querySelectorAll('[id*="_source_phase_"]'));

    if (spans.length === 0) {
        // No plag spans in DOM yet — linear fallback
        paintRect(ctx, width,
            Math.round(scrollTop / scrollHeight * innerWidth) + 1,
            Math.max(2, Math.round(clientHeight / scrollHeight * innerWidth)),
            caretRef);
        return;
    }

    // Build anchor array: [{scrollOffset, charPos}]
    // scrollOffset = span's distance from the top of the scroll container's content.
    // We add TWO anchors per span (top → charStart, bottom → charEnd) so that
    // long plag segments stay accurate and compressed clean-text gaps don't skew
    // the interpolation.
    const containerRect = el.getBoundingClientRect();
    const anchors = [];
    for (const span of spans) {
        // Span IDs are formatted as "{SId}_source_phase_{PIdx}"
        const match = span.id.match(/^(\d+)_source_phase_(\d+)$/);
        if (!match) continue;
        const key = `${match[1]}_${match[2]}`; // "{SId}_{PIdx}"
        const entry = map[key];
        if (entry == null) continue;
        const rect = span.getBoundingClientRect();
        const offsetTop    = Math.max(0, Math.min(scrollHeight, rect.top    - containerRect.top + scrollTop));
        const offsetBottom = Math.max(0, Math.min(scrollHeight, rect.bottom - containerRect.top + scrollTop));
        anchors.push({ scrollOffset: offsetTop,    charPos: entry.start });
        anchors.push({ scrollOffset: offsetBottom, charPos: entry.end   });
    }

    if (anchors.length === 0) {
        paintRect(ctx, width,
            Math.round(scrollTop / scrollHeight * innerWidth) + 1,
            Math.max(2, Math.round(clientHeight / scrollHeight * innerWidth)),
            caretRef);
        return;
    }

    anchors.sort((a, b) => a.scrollOffset - b.scrollOffset);

    // Add boundary anchors at both ends
    anchors.unshift({ scrollOffset: 0, charPos: 0 });
    anchors.push({ scrollOffset: scrollHeight, charPos: totalChars });

    // Store for click→scroll inverse lookup
    if (anchorsRef) anchorsRef.current = { anchors, totalChars, scrollHeight };

    // Interpolate scroll offset → character position
    const scrollToChar = (offset) => {
        if (offset <= anchors[0].scrollOffset) return anchors[0].charPos;
        if (offset >= anchors[anchors.length - 1].scrollOffset) return anchors[anchors.length - 1].charPos;
        let lo = 0, hi = anchors.length - 1;
        while (hi - lo > 1) {
            const mid = (lo + hi) >> 1;
            if (anchors[mid].scrollOffset <= offset) lo = mid;
            else hi = mid;
        }
        const t = (anchors[hi].scrollOffset === anchors[lo].scrollOffset)
            ? 0
            : (offset - anchors[lo].scrollOffset) / (anchors[hi].scrollOffset - anchors[lo].scrollOffset);
        return anchors[lo].charPos + t * (anchors[hi].charPos - anchors[lo].charPos);
    };

    const charAtTop    = scrollToChar(scrollTop);
    const charAtBottom = scrollToChar(scrollTop + clientHeight);

    const rectX = Math.max(1, Math.round(charAtTop    * fact) + 1);
    const rectXEnd = Math.min(width - 1, Math.round(charAtBottom * fact) + 1);
    paintRect(ctx, width, rectX, Math.max(2, rectXEnd - rectX), caretRef);
}

function paintRect(ctx, width, rectX, rectW, caretRef) {
    if (caretRef) caretRef.current = { x: rectX, w: rectW };
    ctx.strokeStyle = 'rgba(25, 118, 210, 0.9)'; // blue frame, no fill
    ctx.lineWidth = 2;
    ctx.strokeRect(rectX + 1, 1, rectW - 2, HEIGHT - 2);
    ctx.lineWidth = 1;
}
